import { Link, useMatch } from 'react-router';
import styled, { css } from 'styled-components';

import { useAuth } from '@contexts/User.context';

const StyledBanner = styled.div`
	margin: 0 0.5rem;
	@media (min-width: 992px) {
		margin-left: 2rem;
		margin-right: 2rem;
	}
`;

const tones = {
	// Friendly nudge while the trial runs
	trial: css`
		background-color: ${props => props.theme.colors.green};
		color: white;
		&:hover,
		&:focus-visible {
			color: white;
			background-color: #146c43;
		}
	`,
	// Something needs the user's attention (trial over, payment failed, …)
	attention: css`
		background-color: #ffc107;
		color: #000;
		&:hover,
		&:focus-visible {
			color: #000;
			background-color: #ffca2c;
		}
	`,
};

const StyledLink = styled(Link)`
	display: block;
	font-size: 0.8rem;
	border-radius: 0.375rem;
	padding: 0.2rem 0.5rem;
	transition: all 0.2s ease-in-out;
	text-decoration: none;
	${props => tones[props.$tone]}
`;

/**
 * Banner copy per subscription view. Returns null when the view has no banner
 * (active, unknown, none, hidden, or a trial without a known end date).
 *
 * @param {{ view: string, daysLeft: number|null }} subscriptionView
 * @returns {{ text: string, action: string, tone: 'trial'|'attention' }|null}
 */
const getBannerCopy = ({ view, daysLeft } = {}) => {
	switch (view) {
		case 'trial_active':
			if (daysLeft == null) return null;
			if (daysLeft === 0)
				return { text: 'Your free trial ends today', action: 'See plans', tone: 'trial' };
			return {
				text:
					daysLeft === 1
						? '1 day left in your free trial'
						: `${daysLeft} days left in your free trial`,
				action: 'See plans',
				tone: 'trial',
			};
		case 'trial_ended':
			return {
				text: 'Your free trial has ended',
				action: 'Choose a plan',
				tone: 'attention',
			};
		case 'past_due':
			return {
				text: 'Your last payment failed',
				action: 'Update payment method',
				tone: 'attention',
			};
		case 'pending':
			return {
				text: "Your payment hasn't been completed",
				action: 'Finish setup',
				tone: 'attention',
			};
		case 'inactive':
			return {
				text: 'Your subscription has ended',
				action: 'Choose a plan',
				tone: 'attention',
			};
		case 'never_started':
			return {
				text: "Your payment wasn't completed",
				action: 'Choose a plan',
				tone: 'attention',
			};
		default:
			return null;
	}
};

/**
 * Top-bar subscription banner (replaces the old trial banner).
 *
 * Driven only by `subscriptionView` from the user context, so it is hidden for
 * premium, organization and admin users. Every banner is one real link to
 * `/subscription`. Display only: it does not gate anything.
 *
 * Not shown on `/checkout`, where it would contradict the page (e.g. a trial
 * banner under "Payment received" while the subscription activates).
 */
const SubscriptionBanner = () => {
	const { subscriptionView } = useAuth();
	const onCheckout = !!useMatch('/checkout');
	const copy = getBannerCopy(subscriptionView);

	if (onCheckout || !copy) return null;

	return (
		<StyledBanner>
			<StyledLink to="/subscription" $tone={copy.tone}>
				{copy.text} <span aria-hidden="true">·</span>{' '}
				<strong>
					{copy.action} <span aria-hidden="true">→</span>
				</strong>
			</StyledLink>
		</StyledBanner>
	);
};

export default SubscriptionBanner;
